import { useState } from "react";
import { useAxios } from "../../../utils/useAxios";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * @typedef {Object} ClassroomType
 * @property {number} id - The classroom ID
 * @property {string} tenant_id - The tenant UUID
 * @property {number} teacher_id - The teacher's ID
 * @property {string} name - Classroom name
 * @property {string|null} description - Classroom description
 * @property {string|null} start_date - Start date in YYYY-MM-DD format
 * @property {string|null} end_date - End date in YYYY-MM-DD format
 * @property {string} status - Classroom status ('0' or '1')
 * @property {string} created_at - Creation timestamp
 * @property {string} updated_at - Update timestamp
 * @property {string} classroom_code - Unique classroom code
 * @property {number} total_students - Total number of students in the classroom
 * @property {number} total_lessons - Total number of lessons in the classroom
 */

/**
 * @typedef {import("../../../common/types/types").PaginationMeta} PaginationMeta
 */

/**
 * @typedef {object} GetClassroomParams
 * @property {number} [page]        - Page number for pagination
 * @property {number} [per_page]    - Number of items per page
 * @property {object} [filters]     - Filters in key:value format
 * @property {string} [sort]        - Sorting (e.g. "name" for ascending or "-name" for descending)
 */

/**
 * Custom hook to handle fetching classrooms with pagination, filtering & sorting
 */
export const useGetAllclassrooms = () => {
  const [classrooms, setClassrooms] = useState(
    /** @type {ClassroomType[] | null | undefined} */(undefined),
  );
  const [meta, setMeta] = useState(
    /** @type {PaginationMeta | null | undefined} */(undefined),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch classrooms with pagination, filtering & sorting
   * @async
   * @param {GetClassroomParams} params
   * @returns {Promise<void>}
   */
  const getAllClassrooms = async ({
    page = 1,
    per_page = 10,
    filters = {},
    sort = "",
    teacher_id = "",
  } = {}) => {
    try {
      setLoading(true);
      setError("");

      // Build Query Params
      const query = new URLSearchParams();

      // Pagination
      query.append("page", page);
      query.append("per_page", per_page);

      // Teacher ID (direct query param)
      if (teacher_id) {
        query.append("teacher_id", teacher_id);
      }

      // Filters
      Object.keys(filters).forEach((field) => {
        if (filters[field] !== "" && filters[field] !== undefined) {
          query.append(`filter[${field}]`, filters[field]);
        }
      });
      // Sorting
      if (sort) query.append("sort", sort);

      const url = `${TENANT_DOMAIN}/classrooms?${query.toString()}`;

      const response = await axiosInstance.get(url);

      setClassrooms(response.data?.data || []);
      setMeta(response.data?.meta || null);
    } catch (err) {
      setClassrooms(null);
      setMeta(null);
      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    classrooms,
    meta,
    loading,
    error,
    getAllClassrooms,
  };
};
